<section class="ppp-rules">
  <header class="ppp-rules__header">
    <h1>Program rules</h1>
    <p class="ppp-rules__subtitle">How carrier and driver eligibility is determined, and how bonuses are paid.</p>
  </header>

  <div class="ppp-rules__cards">
    <article class="ppp-rules__card">
      <h2>Carrier eligibility</h2>
      <p>
        Every Monday we look at how your drivers ran last week in each region. To stay in the program for the upcoming
        week, your weekend coverage has to keep up with your weekday peak.
      </p>
      <ul>
        <li>
          <strong>Daily capacity</strong> — your busiest weekend day (Fri-Sun) must have at least
          <strong>90% as many drivers</strong> as your busiest weekday (Mon-Thu). If you drop coverage on weekends by
          more than 10% relative to your weekday peak, you fall out of the program for the next week.
        </li>
      </ul>
      <p class="ppp-rules__note">
        If you didn't run any drivers in a region last week, that region is skipped for the evaluation — you're not
        penalized for not operating there.
      </p>
    </article>

    <article class="ppp-rules__card">
      <h2>Driver rules (per shift)</h2>
      <p>
        At the end of every shift we check the driver against four rules. The driver has to clear
        <em>all four</em> to earn the bonus on that shift, and your carrier has to be eligible too — both have to line
        up.
      </p>
      <ul>
        <li>
          <strong>No rejected loads</strong> — the driver can't reject any load that gets dispatched to them during the
          shift.
        </li>
        <li>
          <strong>No missed loads</strong> — when a load is dispatched, the driver has to accept it within the cutoff.
          If they ignore the dispatch and we have to flick the load to someone else, that counts as a missed load for
          this rule.
        </li>
        <li>
          <strong>GPS always on</strong> — the driver app has to send at least one GPS ping per minute while a load is
          in progress. Loads that come back with too few pings count as GPS off.
        </li>
        <li>
          <strong>On time to pickup</strong> — the driver can't arrive at pickup more than 2× their predicted ETA AND
          more than 2 hours late. Late arrivals are tracked automatically.
        </li>
      </ul>
      <p class="ppp-rules__note">
        Even one violation on any of these four rules costs the driver the bonus for that shift.
      </p>
    </article>

    <article class="ppp-rules__card ppp-rules__card--flow">
      <h2>How and when the bonus is paid</h2>
      <p>
        To make sure the bonus only goes to drivers who actually earned it, we wait until the full shift is done before
        deciding. The first load of every Preferred Partner shift goes on a temporary hold so it doesn't invoice while
        we're still waiting on the rest of the shift.
      </p>
      <ol class="ppp-rules__flow">
        <li>
          <strong>First load held back.</strong> As soon as the driver completes the first load of the shift, that load
          is placed on hold and stops short of invoicing.
        </li>
        <li>
          <strong>The hold stays on while the shift runs.</strong> Every other load on the same shift flows normally —
          only that first one waits. This is the window during which the driver's performance is being measured against
          the four rules.
        </li>
        <li>
          <strong>End-of-shift check (~25 min after clock-out).</strong> Once the shift closes we pull the full shift
          data — rejections, missed acceptances, GPS coverage, late arrivals — and run the four driver checks.
        </li>
        <li>
          <strong>Hold released, bonus decided.</strong> If the driver passes all four <em>and</em> your carrier is
          eligible for that week, the bonus is added to every load on that shift and the hold comes off. If any check
          fails, the hold is still released — just without the bonus.
        </li>
      </ol>
      <p class="ppp-rules__note">
        Bonuses are never applied in real time. Everything settles at the end of the shift, so seeing the first load
        briefly held is expected — it isn't something you need to approve or resolve manually.
      </p>
    </article>

    <article class="ppp-rules__card ppp-rules__card--thresholds" *ngIf="programConfig$ | async as cfg; else loadingTpl">
      <h2>Thresholds in effect</h2>
      <ng-container *ngIf="cfg.regions?.length; else noConfigTpl">
        <div class="ppp-rules__region" *ngFor="let region of cfg.regions; trackBy: trackByRegion">
          <header>
            <h3>{{ region.regionName }}</h3>
            <span class="ppp-rules__allowlist" *ngIf="!region.inAllowlist">Not in allowlist</span>
          </header>

          <ng-container *ngIf="region.programConfig as pc; else regionEmpty">
            <div class="ppp-rules__metrics">
              <div>
                <span class="ppp-rules__metric-value">{{ pc.shiftFloorCents | centsToDollars }}</span>
                <span class="ppp-rules__metric-label">Shift floor (per shift)</span>
              </div>
            </div>

            <h4>Load tiers</h4>
            <p class="ppp-rules__tiers-hint">
              Bonus percentage applied to each completed load on a qualifying shift. The first load uses tier 1, the
              second uses tier 2, and so on.
            </p>
            <table mat-table [dataSource]="pc.loadTiers" class="ppp-rules__tiers">
              <ng-container matColumnDef="position">
                <th mat-header-cell *matHeaderCellDef>Load #</th>
                <td mat-cell *matCellDef="let tier">{{ tier.loadPosition }}</td>
              </ng-container>
              <ng-container matColumnDef="rate">
                <th mat-header-cell *matHeaderCellDef>Bonus rate</th>
                <td mat-cell *matCellDef="let tier">{{ tier.rateBasisPoints / 100 | number: '1.2-2' }}%</td>
              </ng-container>
              <tr mat-header-row *matHeaderRowDef="['position', 'rate']"></tr>
              <tr mat-row *matRowDef="let tier; columns: ['position', 'rate']"></tr>
            </table>
          </ng-container>

          <ng-template #regionEmpty>
            <p class="ppp-rules__empty">No active program for this region.</p>
          </ng-template>
        </div>
      </ng-container>

      <ng-template #noConfigTpl>
        <p class="ppp-rules__empty">No regions are currently part of the program for this carrier.</p>
      </ng-template>
    </article>
  </div>

  <ng-template #loadingTpl>
    <article class="ppp-rules__card ppp-rules__card--loading">Loading thresholds…</article>
  </ng-template>
</section>
